/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Renders one report chart.
 *
 * Takes the run result (columns + rows) and a chart config, and draws it. It
 * knows nothing about which report it is showing — the column descriptors the
 * engine returns carry the types — which is what lets one component serve all
 * 48 templates plus anything a user builds.
 *
 * Chart.js directly rather than the wrappers in src/examples/Charts: those
 * take a single `color` name and generate one gradient, so they cannot colour
 * a slice per category, and they expect data pre-shaped into their own
 * {labels, datasets} form. Recharts is in package.json but imported nowhere,
 * so using it here would introduce a second charting library to the bundle
 * for no gain.
 */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import {
  ArcElement, BarElement, CategoryScale, Chart as ChartJS, Filler, Legend,
  LineElement, LinearScale, PointElement, Tooltip,
} from 'chart.js';
import { Bar, Doughnut, Line, Pie } from 'react-chartjs-2';

import colors from 'styles/tokens/colors';
import { alpha, compact, displayValue, formatValue, paletteFor } from './chartPalette';

ChartJS.register(
  ArcElement, BarElement, CategoryScale, Filler, Legend, LineElement,
  LinearScale, PointElement, Tooltip,
);

const AXIS_COLOR = colors.text.light;
const GRID_COLOR = alpha(colors.border.default, 0.7);
const SURFACE = colors.background.white;

export default function ReportChart({ result, config, chartType, height, currency }) {
  const model = useMemo(
    () => buildModel(result, config, chartType, currency),
    [result, config, chartType, currency],
  );

  if (!model) {
    return (
      <Box
        sx={{
          height: height || 260,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '1px dashed', borderColor: 'divider', borderRadius: '12px',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Nothing to chart yet — group the report and add a summary first.
        </Typography>
      </Box>
    );
  }

  const { data, options, kind, summary } = model;
  const Component = { pie: Pie, donut: Doughnut, line: Line, area: Line }[kind] || Bar;

  return (
    // Chart.js draws to a canvas, which is opaque to a screen reader — without
    // a label the whole chart is announced as nothing at all. role="img" plus
    // a text summary of the series is the minimum that makes it perceivable;
    // the report's Table tab remains the full accessible alternative.
    <Box
      sx={{ height: height || 260, position: 'relative' }}
      role="img"
      aria-label={summary}
    >
      <Component data={data} options={options} />
    </Box>
  );
}

ReportChart.propTypes = {
  result: PropTypes.object,
  config: PropTypes.object,
  chartType: PropTypes.string,
  height: PropTypes.number,
  currency: PropTypes.string,
};

/**
 * Turn a run result into Chart.js data and options, or null when the config
 * does not describe something drawable.
 *
 * Returning null rather than throwing: a dashboard full of tiles should show
 * one empty tile with an explanation, not lose the page to an error boundary.
 */
function buildModel(result, config, chartType, currency) {
  const rows = result?.rows;
  const columns = result?.columns;
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(columns)) return null;

  const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));

  // Fall back to the first non-aggregate column for the category and the first
  // aggregate for the value, so a chart added before its bindings were chosen
  // still draws something sensible.
  const categoryKey = config?.category_key
    ?? columns.find((c) => !c.is_aggregate)?.key;
  const valueKey = config?.value_key
    ?? columns.find((c) => c.is_aggregate)?.key;
  if (!categoryKey || !valueKey) return null;

  const categoryColumn = byKey[categoryKey];
  const valueColumn = byKey[valueKey];
  if (!categoryColumn || !valueColumn) return null;

  const resolved = result.labels;
  const labels = rows.map(
    (row) => displayValue(row[categoryKey], categoryColumn, currency, resolved),
  );
  const values = rows.map((row) => Number(row[valueKey] ?? 0));

  const kind = chartType === 'funnel' ? 'horizontal_bar' : (chartType || 'bar');
  const circular = kind === 'pie' || kind === 'donut';
  const horizontal = kind === 'horizontal_bar';
  const palette = paletteFor(labels);

  // A line or area is one series in one colour; everything else colours per
  // category, which is what makes a status breakdown readable.
  const seriesColor = colors.brand.primary;
  const dataset = circular
    ? {
        data: values,
        backgroundColor: palette,
        borderColor: SURFACE,
        borderWidth: 2,
        hoverOffset: 6,
      }
    : kind === 'line' || kind === 'area'
      ? {
          label: valueColumn.label,
          data: values,
          borderColor: seriesColor,
          backgroundColor: kind === 'area' ? alpha(seriesColor, 0.16) : 'transparent',
          fill: kind === 'area',
          tension: 0.35,
          pointRadius: values.length > 30 ? 0 : 3,
          pointHoverRadius: 5,
          borderWidth: 2,
        }
      : {
          label: valueColumn.label,
          data: values,
          backgroundColor: palette,
          borderRadius: 6,
          maxBarThickness: 42,
        };

  const formatTick = (raw) =>
    valueColumn.type === 'currency'
      ? `${currencySymbol(currency)}${compact(raw)}`
      : compact(raw);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: horizontal ? 'y' : 'x',
    plugins: {
      legend: {
        // A single-series bar chart's legend says only what the axis already
        // says, and costs a row of vertical space the chart needs more.
        display: circular,
        position: 'bottom',
        labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true,
                  padding: 14, color: AXIS_COLOR, font: { size: 11 } },
      },
      tooltip: {
        callbacks: {
          // Put the category in context ("Trip: Bali Escape") so a bar is not
          // just a colour with a currency — especially when the axis truncates.
          title: (items) => {
            const label = items?.[0]?.label;
            if (label == null || label === '') return '';
            const kind = categoryColumn.label || '';
            if (!kind) return String(label);
            const text = String(label);
            if (text.toLowerCase().startsWith(String(kind).toLowerCase())) {
              return text;
            }
            return `${kind}: ${text}`;
          },
          label: (ctx) => {
            const raw = circular ? ctx.parsed : (horizontal ? ctx.parsed.x : ctx.parsed.y);
            const formatted = formatValue(raw, valueColumn, currency);
            if (!circular) {
              return ` ${valueColumn.label}: ${formatted}`;
            }
            const total = values.reduce((sum, v) => sum + v, 0);
            const share = total ? Math.round((raw / total) * 100) : 0;
            return ` ${valueColumn.label}: ${formatted} (${share}%)`;
          },
        },
      },
    },
    scales: circular ? undefined : {
      x: {
        grid: { display: horizontal, color: GRID_COLOR, drawBorder: false },
        ticks: {
          color: AXIS_COLOR, font: { size: 11 },
          callback: horizontal
            ? formatTick
            // Long category labels rotate rather than wrap; truncating keeps
            // the plot area from collapsing on a chart of account names.
            : function tick(value) {
                const label = String(this.getLabelForValue(value));
                return label.length > 16 ? `${label.slice(0, 15)}…` : label;
              },
          maxRotation: horizontal ? 0 : 45,
        },
      },
      y: {
        beginAtZero: true,
        grid: { display: !horizontal, color: GRID_COLOR, drawBorder: false },
        ticks: {
          color: AXIS_COLOR, font: { size: 11 },
          callback: horizontal
            ? function tick(value) {
                const label = String(this.getLabelForValue(value));
                return label.length > 22 ? `${label.slice(0, 21)}…` : label;
              }
            : formatTick,
        },
      },
    },
    cutout: kind === 'donut' ? '62%' : undefined,
  };

  // Read out the largest few categories rather than every one: a 40-bar chart
  // announced in full is not more accessible than one announced in summary.
  const ranked = labels
    .map((label, index) => ({ label, value: values[index] }))
    .sort((a, b) => b.value - a.value);
  const spoken = ranked.slice(0, 5)
    .map((r) => `${r.label}: ${formatValue(r.value, valueColumn, currency)}`)
    .join('; ');
  const summary =
    `${kind.replace('_', ' ')} chart of ${valueColumn.label} by `
    + `${categoryColumn.label}. ${labels.length} categor`
    + `${labels.length === 1 ? 'y' : 'ies'}. `
    + (ranked.length > 5 ? `Largest five — ${spoken}.` : `${spoken}.`);

  return { kind, data: { labels, datasets: [dataset] }, options, summary };
}

function currencySymbol(code) {
  try {
    return (0).toLocaleString(undefined, {
      style: 'currency', currency: code || 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).replace(/[\d\s.,]/g, '');
  } catch {
    return '';
  }
}
